import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  FloorPlanId,
  OpeningId,
  RoomId,
  UnitPositionId,
  UnitTypeId,
  WallId,
} from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import { canBeJoined, bathroomCannotBeJoinedError, GEOMETRY_DECIMALS, type Room } from './room';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';
import { VOLUME_DECIMALS } from './structural-element';

/**
 * Parede (RN-158).
 *
 * Existe **entre** ambientes, e é por isso que ela é entidade em vez de um lado do
 * retângulo do ambiente (ADR-109). Somar perímetros contaria a parede entre o quarto e a
 * sala duas vezes: certo para o reboco, que reveste as duas faces; errado pela metade
 * para a alvenaria, que assenta um bloco só. Com a parede existindo uma vez, as duas
 * contas saem do mesmo desenho e nenhuma precisa de fator de correção.
 */

/**
 * Um lado da parede.
 *
 * `positionId` só aparece quando a parede é **divisa** dentro do pavimento tipo: aí o
 * lado não pertence a uma tipologia em abstrato, e sim ao apto que ocupa aquela posição —
 * duas posições podem usar a mesma tipologia (aptos 01 e 03, ambos Tipologia A), e sem a
 * posição não haveria como saber de quem é cada metade.
 */
export interface WallSide {
  readonly roomId: RoomId;
  readonly positionId?: UnitPositionId;
  /**
   * Qual lado do contorno **deste** ambiente a parede é — o índice em `polygonEdges` (RN-243).
   *
   * A parede não guarda geometria (ADR-109), e continua sem guardar: guarda o lado (ADR-144).
   * O desenho a punha no lugar procurando, entre os lados do contorno, um do mesmo comprimento. Com quatro lados de 3 m qualquer um
   * servia; depois de corrigir a medida no painel ou empurrar um lado, nenhum servia — e a
   * parede sumia do desenho, contada no painel e somada no total (D22). Lembrar o lado em que
   * nasceu tira a medida da pergunta "onde ela está".
   *
   * É do **lado**, e não da parede, porque a casada tem dois: o 2 da sala é o 4 da cozinha.
   * Ausente é a parede de antes do índice (RN-244), a acrescentada à mão no painel e a do
   * ambiente digitado sem contorno — para essas o desenho continua procurando pela medida.
   *
   * Não é chave de nada: um lado do contorno poderá ter mais de uma parede quando a
   * sobreposição parcial for cortada (Fase 24, fatia 8).
   */
  readonly edgeIndex?: number;
  /**
   * Que trecho do lado `edgeIndex` a parede ocupa — ausente é o lado inteiro (RN-304).
   *
   * Existe por causa do casamento parcial: a sala de 6 m com um quarto de 3 m encostado no
   * meio tem **três** paredes naquele lado — 1,5 m livre, 3 m casada com o quarto, 1,5 m
   * livre. Casar a parede inteira fazia a sala ganhar ou perder 3 m de alvenaria, ou deixava
   * duas paredes uma sobre a outra, as duas contadas (D24). Com o trecho, cada pedaço sabe
   * onde está no lado, e o desenho o põe ali.
   *
   * Só faz sentido com `edgeIndex`: trecho de lado nenhum é desenho que ninguém fez.
   */
  readonly span?: EdgeSpan;
}

/**
 * O trecho de um lado do contorno, em **fração** do lado: `0` é o canto em que o lado começa
 * (o `from` de `polygonEdges`), `1` o canto em que acaba (ADR-157).
 *
 * Fração, e não metro, porque o lado muda de tamanho sem mudar de lugar: recalibrar a
 * prancha estica o desenho inteiro, e o trecho em metro teria de ser reescalado junto —
 * mais um número a lembrar de corrigir em cada caminho que mexe na escala. Em fração, o
 * pedaço do meio continua sendo o meio.
 */
export interface EdgeSpan {
  readonly start: number;
  readonly end: number;
}

/** O lado inteiro — o que a parede sem trecho ocupa. */
export const WHOLE_EDGE: EdgeSpan = { start: 0, end: 1 };

/**
 * Até onde duas frações são o mesmo ponto do lado.
 *
 * O trecho é gravado com seis casas (a `0047`); a sobra disso é arredondamento, e não um
 * pedaço de parede de um milésimo de milímetro entre dois vizinhos.
 */
export const SPAN_TOLERANCE = 1e-5;

/** Um trecho que existe: dentro do lado, e começando antes de acabar. */
export function isEdgeSpan(span: EdgeSpan): boolean {
  return (
    Number.isFinite(span.start) &&
    Number.isFinite(span.end) &&
    span.start >= 0 &&
    span.end <= 1 &&
    span.end - span.start > SPAN_TOLERANCE
  );
}

/** O trecho que cobre o lado inteiro — e que por isso é o mesmo que não ter trecho. */
export function isWholeEdge(span: EdgeSpan): boolean {
  return span.start <= SPAN_TOLERANCE && span.end >= 1 - SPAN_TOLERANCE;
}

/** O trecho que este lado da parede ocupa, com o lado inteiro para quem não tem. */
export function spanOf(side: WallSide | undefined): EdgeSpan {
  return side?.span ?? WHOLE_EDGE;
}

/**
 * De quem é a parede.
 *
 * Parede interna à tipologia viaja com ela: é cadastrada uma vez e vale em todos os aptos
 * que usam aquela tipologia. **Divisa é do pavimento tipo** (RN-163), porque ela depende
 * de quem está do outro lado — e quem está do outro lado é a posição, não a tipologia.
 */
export type WallOwner =
  | { readonly kind: 'UNIT_TYPE'; readonly unitTypeId: UnitTypeId }
  | { readonly kind: 'FLOOR_PLAN'; readonly floorPlanId: FloorPlanId };

export type OpeningKind = 'PORTA' | 'JANELA' | 'PASSAGEM';

export const OPENING_KIND_LABEL: Readonly<Record<OpeningKind, string>> = {
  PORTA: 'Porta',
  JANELA: 'Janela',
  PASSAGEM: 'Passagem',
};

/**
 * Vão — declarado **uma única vez**, na parede (RN-160).
 *
 * Se cada ambiente declarasse o seu, a porta entre o quarto e a circulação seria
 * descontada duas vezes da alvenaria. Morando na parede, ela desconta uma vez do bloco e
 * uma vez de cada face — que é exatamente o que a obra faz.
 */
export interface Opening {
  readonly id: OpeningId;
  readonly wallId: WallId;
  readonly kind: OpeningKind;
  /** Metros. */
  readonly width: number;
  readonly height: number;
  /** Vãos iguais na mesma parede — duas janelas de 1,20 são um registro com `count: 2`. */
  readonly count: number;
  /**
   * Onde, ao longo da parede, está o **centro** do vão — em metros a partir do início dela.
   *
   * O vão cadastrado no cartão não sabe em que ponto da parede fica, e o desenho o
   * distribui, tracejado, como palpite. O inserido pela prancha sabe: foi clicado ali, e
   * redistribuí-lo seria repetir com a porta o salto que o pilar dava. Ausente continua
   * sendo o palpite.
   */
  readonly offset?: number;
}

export interface Wall {
  readonly id: WallId;
  readonly owner: WallOwner;
  readonly sideA: WallSide;
  /** Ausente = parede externa: do outro lado é a rua (RN-159). */
  readonly sideB?: WallSide;
  /** Metros. */
  readonly length: number;
  /**
   * Altura **estrutural**, até a laje (RN-162).
   *
   * Independente do pé-direito do ambiente de propósito: gesso rebaixado a 2,50 não
   * encurta a alvenaria de 2,90. E parede entre ambientes de pé-direito diferente teria
   * duas alturas se a altura viesse do ambiente — não tem, tem uma.
   */
  readonly height: number;
  /** Metros. Distingue a parede de 9 da de 15 — e é o que o serviço por volume vai pedir. */
  readonly thickness?: number;
  /**
   * Conjugada: a parede **não existe** (RN-161).
   *
   * Fica registrada em vez de ser apagada porque a vizinhança continua sendo um fato — é
   * o que permite desconjugar depois sem redesenhar a tipologia. Não contribui com
   * alvenaria nem com face: onde não há parede não há bloco nem reboco.
   */
  readonly joined: boolean;
  /**
   * Guarda-corpo sobre a mureta — a altura dele, em metros (Fase 24, D25).
   *
   * A mureta da varanda é parede como as outras, com a altura dela (`height`): soma bloco,
   * face e rodapé. O que ela tem a mais é o guarda-corpo em cima, e ele é medido como o do
   * lado sem parede — comprimento, área e contagem —, com a altura **dele**, e não a da
   * mureta. Um número só serve de marcador e de medida: ausente é parede sem guarda-corpo.
   */
  readonly railingHeight?: number;
  /**
   * O nome que alguém deu a esta parede, quando deu.
   *
   * Ausente é o comum, e a tela então a chama pelos ambientes que ela separa — "Sala ↔
   * Quarto 1". O campo existe porque esse nome automático nem sempre serve: a parede da
   * escada, a que leva o quadro de luz, a que o projeto nomeia. Quem renomeia espera que
   * o nome sobreviva à recarga, e sem uma coluna ele não sobreviveria.
   */
  readonly label?: string;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateWallInput {
  readonly id: WallId;
  readonly owner: WallOwner;
  readonly sideA: WallSide;
  readonly sideB?: WallSide;
  readonly length: number;
  readonly height: number;
  readonly thickness?: number;
  readonly railingHeight?: number;
  readonly label?: string;
  readonly notes?: string;
  readonly now: string;
}

function positive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function createWall(input: CreateWallInput): Result<Wall, DomainError> {
  if (!positive(input.length)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'O comprimento da parede deve ser maior que zero.',
        { length: input.length },
      ),
    );
  }
  if (!positive(input.height)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_HEIGHT,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_HEIGHT]!,
        { height: input.height },
      ),
    );
  }
  if (input.thickness !== undefined && !positive(input.thickness)) {
    return err(
      domainError(TAKEOFF_ERROR.INVALID_DIMENSION, 'A espessura deve ser maior que zero.', {
        thickness: input.thickness,
      }),
    );
  }
  if (input.railingHeight !== undefined && !positive(input.railingHeight)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_HEIGHT,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_HEIGHT]!,
        { railingHeight: input.railingHeight },
      ),
    );
  }
  if (input.sideB !== undefined && sameSide(input.sideA, input.sideB)) {
    return err(
      domainError(
        TAKEOFF_ERROR.WALL_SIDES_IDENTICAL,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.WALL_SIDES_IDENTICAL]!,
      ),
    );
  }

  return ok({
    id: input.id,
    owner: input.owner,
    sideA: ladoConferido(input.sideA),
    length: input.length,
    height: input.height,
    joined: false,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.sideB === undefined ? {} : { sideB: ladoConferido(input.sideB) }),
    ...(input.thickness === undefined ? {} : { thickness: input.thickness }),
    ...(input.railingHeight === undefined ? {} : { railingHeight: input.railingHeight }),
    ...(input.label === undefined ? {} : { label: input.label }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

function sameSide(a: WallSide, b: WallSide): boolean {
  return a.roomId === b.roomId && a.positionId === b.positionId;
}

/** Um lado do contorno é um inteiro a partir do zero — o primeiro lado é o 0. */
export function isEdgeIndex(value: number): boolean {
  return Number.isInteger(value) && value >= 0;
}

/**
 * O lado sem o índice que não aponta lado nenhum.
 *
 * Negativo ou quebrado é desenho que ninguém fez, e fica de fora como o `offset` do vão:
 * recusar a parede inteira por isso seria perder alvenaria por causa de um campo que tem
 * plano B — sem o índice, o desenho volta a procurá-la pela medida.
 */
function ladoConferido(side: WallSide): WallSide {
  const comIndice: WallSide =
    side.edgeIndex === undefined || isEdgeIndex(side.edgeIndex)
      ? side
      : (({ edgeIndex: _fora, ...resto }) => resto)(side);
  // O trecho vai pelo mesmo critério (RN-304): sem lado, fora do lado ou do tamanho do lado
  // inteiro, ele não diz nada que a ausência não diga — e sem ele a parede ocupa o lado todo.
  if (comIndice.span === undefined) return comIndice;
  if (
    comIndice.edgeIndex !== undefined &&
    isEdgeSpan(comIndice.span) &&
    !isWholeEdge(comIndice.span)
  ) {
    return comIndice;
  }
  const { span: _semTrecho, ...semTrecho } = comIndice;
  return semTrecho;
}

/**
 * O lado da parede que dá para este ambiente.
 *
 * A casada tem dois, e cada um lembra o lado do contorno **do seu** ambiente (RN-243): a
 * pergunta "em que lado da sala esta parede está" só tem resposta olhando o lado da sala.
 */
export function wallSideOf(
  wall: Pick<Wall, 'sideA' | 'sideB'>,
  roomId: RoomId,
): WallSide | undefined {
  if (wall.sideA.roomId === roomId) return wall.sideA;
  return wall.sideB?.roomId === roomId ? wall.sideB : undefined;
}

/**
 * A mesma parede, lembrando outro lado do contorno deste ambiente — ou nenhum.
 *
 * `undefined` **tira** a chave em vez de gravá-la vazia: ausente é o que o desenho lê como
 * "procure pela medida", e é o que o contorno redesenhado pede quando o lado antigo deixou
 * de existir.
 */
export function withEdgeIndex(wall: Wall, roomId: RoomId, edgeIndex: number | undefined): Wall {
  const trocar = (side: WallSide): WallSide => {
    // O trecho é fração **daquele** lado (RN-304): mudou o lado, ele não aponta mais nada, e
    // sai junto. No mesmo lado ele fica — é o empurrão que confirma o índice.
    const { edgeIndex: antigo, span, ...resto } = side;
    const comTrecho = span !== undefined && antigo === edgeIndex ? { span } : {};
    return edgeIndex === undefined ? resto : { ...resto, edgeIndex, ...comTrecho };
  };
  if (wall.sideA.roomId === roomId) return { ...wall, sideA: trocar(wall.sideA) };
  if (wall.sideB?.roomId === roomId) return { ...wall, sideB: trocar(wall.sideB) };
  return wall;
}

/**
 * A mesma parede, ocupando outro trecho do lado dela neste ambiente — ou o lado inteiro.
 *
 * O trecho do tamanho do lado **sai**, em vez de ficar gravado como `0 → 1`: é o que a
 * parede de antes do corte sempre foi, e as duas formas para a mesma coisa fariam o
 * rascunho parecer mudado sem ter mudado.
 */
export function withEdgeSpan(wall: Wall, roomId: RoomId, span: EdgeSpan | undefined): Wall {
  const trocar = (side: WallSide): WallSide => {
    const { span: _antigo, ...resto } = side;
    return span === undefined || isWholeEdge(span) || resto.edgeIndex === undefined
      ? resto
      : { ...resto, span };
  };
  if (wall.sideA.roomId === roomId) return { ...wall, sideA: trocar(wall.sideA) };
  if (wall.sideB?.roomId === roomId) return { ...wall, sideB: trocar(wall.sideB) };
  return wall;
}

export function createOpening(input: {
  readonly id: OpeningId;
  readonly wallId: WallId;
  readonly kind: OpeningKind;
  readonly width: number;
  readonly height: number;
  readonly count?: number;
  readonly offset?: number;
}): Result<Opening, DomainError> {
  const count = input.count ?? 1;
  if (!positive(input.width) || !positive(input.height)) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'O vão deve ter largura e altura maiores que zero.',
        {
          width: input.width,
          height: input.height,
        },
      ),
    );
  }
  if (!Number.isInteger(count) || count <= 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_DIMENSION,
        'A quantidade de vãos deve ser um inteiro positivo.',
        {
          count,
        },
      ),
    );
  }
  return ok({
    id: input.id,
    wallId: input.wallId,
    kind: input.kind,
    width: input.width,
    height: input.height,
    count,
    // Zero é o começo da parede; negativo é desenho que ninguém fez, e fica de fora.
    ...(input.offset === undefined || !(Number.isFinite(input.offset) && input.offset >= 0)
      ? {}
      : { offset: input.offset }),
  });
}

/** Parede externa: sem segundo ambiente, o outro lado é a rua (RN-159). */
export function isExternal(wall: Pick<Wall, 'sideB'>): boolean {
  return wall.sideB === undefined;
}

/**
 * Divisa entre dois aptos — a que rateia meio a meio (RN-163).
 *
 * É divisa quando os dois lados estão em **posições diferentes** do pavimento tipo. Dois
 * ambientes da mesma tipologia, ou do mesmo apto, não dividem nada com ninguém: a parede
 * é inteira de quem a contém.
 */
export function isPartyWall(wall: Pick<Wall, 'sideA' | 'sideB'>): boolean {
  if (wall.sideB === undefined) return false;
  const a = wall.sideA.positionId;
  const b = wall.sideB.positionId;
  return a !== undefined && b !== undefined && a !== b;
}

export function wallGrossArea(wall: Pick<Wall, 'length' | 'height'>): number {
  return roundQuantity(wall.length * wall.height, GEOMETRY_DECIMALS);
}

export function openingsArea(openings: readonly Opening[]): number {
  const total = openings.reduce(
    (sum, opening) => sum + opening.width * opening.height * opening.count,
    0,
  );
  return roundQuantity(total, GEOMETRY_DECIMALS);
}

/**
 * O vão chega ao piso?
 *
 * Porta e passagem cortam o rodapé; janela não. Ela começa no peitoril, e o rodapé corre
 * inteiro por baixo dela — descontá-la encurtaria a compra de um material que a obra vai
 * assentar.
 */
export function reachesFloor(kind: OpeningKind): boolean {
  return kind !== 'JANELA';
}

/**
 * A largura dos vãos que interrompem o rodapé.
 *
 * É **largura**, e não área: o rodapé é medida linear, e o que ele perde na porta é a
 * largura dela — a altura do vão não tem nada com isso.
 */
export function floorOpeningsWidth(openings: readonly Opening[]): number {
  const total = openings.reduce(
    (sum, opening) => (reachesFloor(opening.kind) ? sum + opening.width * opening.count : sum),
    0,
  );
  return roundQuantity(total, GEOMETRY_DECIMALS);
}

/**
 * Área líquida da parede: bruta menos os vãos e menos a estrutura, e nunca negativa.
 *
 * Vão maior que a parede é erro de digitação, e o lugar de recusá-lo é a validação — não
 * a conta, que aqui devolveria alvenaria negativa e a somaria ao total da obra.
 *
 * `structureArea` é a sombra do pilar e da viga no plano desta parede (RN-182): onde há
 * concreto não há bloco. É zero em toda parede sem estrutura cadastrada, que é como toda
 * parede nasce — e é a diferença entre cobrar alvenaria embaixo da viga e não cobrar.
 */
export function wallNetArea(
  wall: Pick<Wall, 'length' | 'height' | 'joined'>,
  openings: readonly Opening[],
  structureArea = 0,
): number {
  if (wall.joined) return 0;
  const net = wallGrossArea(wall) - openingsArea(openings) - structureArea;
  return net > 0 ? roundQuantity(net, GEOMETRY_DECIMALS) : 0;
}

/**
 * O volume da parede: a área líquida × a espessura (Fase 24, fatia 10).
 *
 * Parte da **líquida**, a mesma da alvenaria — sem os vãos e sem a sombra da estrutura —,
 * porque o que a obra compra é o bloco que fica. Parede sem espessura cadastrada devolve
 * zero, e não um palpite: a espessura é opcional desde a Fase 14, e inventar 15 cm aqui
 * somaria metros cúbicos que ninguém mediu (é a escolha de `runInWall`).
 */
export function wallVolume(wall: Pick<Wall, 'thickness'>, netArea: number): number {
  if (wall.thickness === undefined) return 0;
  return roundQuantity(netArea * wall.thickness, VOLUME_DECIMALS);
}

/** A área do vão — largura × altura, vezes quantos o registro declara. */
export function areaOfOpening(opening: Pick<Opening, 'width' | 'height' | 'count'>): number {
  return opening.width * opening.height * opening.count;
}

/**
 * O contorno do vão — o batente da porta, o requadro da janela (Fase 24, fatia 10).
 *
 * É o que **guarnece** o vão, e a soleira não é guarnecida: o vão que chega ao piso (porta e
 * passagem, `reachesFloor`) tem duas alturas e uma largura — `2A + L`; o que começa no
 * peitoril (janela) fecha o retângulo — `2A + 2L`. A mesma pergunta que já decide o rodapé
 * decide o contorno, e uma segunda lista dela envelheceria separada.
 */
export function contourOfOpening(
  opening: Pick<Opening, 'kind' | 'width' | 'height' | 'count'>,
): number {
  const larguras = reachesFloor(opening.kind) ? 1 : 2;
  return (2 * opening.height + larguras * opening.width) * opening.count;
}

/** A largura somada do vão — a soleira da porta, o peitoril da janela. */
export function widthOfOpening(opening: Pick<Opening, 'width' | 'count'>): number {
  return opening.width * opening.count;
}

export function openingsFitError(wallId: WallId): DomainError {
  return domainError(
    TAKEOFF_ERROR.OPENING_LARGER_THAN_WALL,
    TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.OPENING_LARGER_THAN_WALL]!,
    { wallId },
  );
}

/**
 * Conjuga os dois ambientes: a parede deixa de existir (RN-161).
 *
 * Recusa parede externa — não se conjuga com a rua — e recusa banheiro, que sempre tem
 * parede. Os ambientes vêm por parâmetro porque a regra é sobre o **tipo** deles, e a
 * parede guarda apenas os ids.
 */
export function joinWall(
  wall: Wall,
  sideARoom: Pick<Room, 'id' | 'kind'>,
  sideBRoom: Pick<Room, 'id' | 'kind'>,
  now: string,
): Result<Wall, DomainError> {
  if (isExternal(wall)) {
    return err(
      domainError(
        TAKEOFF_ERROR.WALL_NOT_FOUND,
        'Parede externa não pode ser conjugada: do outro lado é a rua.',
        { wallId: wall.id },
      ),
    );
  }
  if (!canBeJoined(sideARoom.kind) || !canBeJoined(sideBRoom.kind)) {
    return err(bathroomCannotBeJoinedError());
  }
  if (wall.joined) return ok(wall);
  return ok({ ...wall, joined: true, updatedAt: now });
}

/** Desfaz a conjugação — a parede volta a existir. */
export function splitWall(wall: Wall, now: string): Wall {
  if (!wall.joined) return wall;
  return { ...wall, joined: false, updatedAt: now };
}
